/**
 * Diagnostics Store
 * Singleton store for capturing diagnostics and telemetry data (no PII)
 */

/* global Office, OfficeRuntime */

import { BUILD_INFO } from "../buildInfo";
import { STORAGE_KEYS } from "../constants";
import { logDebug } from "../logging";
import type {
  DiagnosticCallRecord,
  DiagnosticError,
  DiagnosticErrorCategory,
  DiagnosticOpStats,
  DiagnosticsSnapshot,
  InternalOpStats,
} from "./types";

// Storage schema version for future evolution
const STORAGE_SCHEMA_VERSION = 1;

class DiagnosticsStore {
  // Per-operation stats (internal format)
  private opStats = new Map<string, InternalOpStats>();

  // In-flight calls tracking
  private inFlightCalls = new Map<
    string,
    { startTime: number; op_key: string; endpoint: string }
  >();

  // Ring buffer for recent calls (max 25)
  private recentCalls: DiagnosticCallRecord[] = [];
  private readonly MAX_RECENT_CALLS = 25;

  // Last error and request
  private lastError: DiagnosticError | null = null;
  private lastRequest: {
    request_id: string;
    op_key: string;
    endpoint: string;
    ms: number;
    ok: boolean;
  } | null = null;

  // Session tracking
  private sessionStartedAt: string;
  private callIdSeq = 0;
  private initialized = false;

  // Cache metrics
  private cacheMetrics = new Map<string, number>();
  private cacheEvents: Array<{ at_iso: string; event: string; key?: string }> = [];
  private readonly MAX_CACHE_EVENTS = 20;

  constructor() {
    this.sessionStartedAt = new Date().toISOString();
  }

  /**
   * Initialize the store (idempotent, never throws)
   * Loads persisted data from OfficeRuntime.storage if available
   */
  async init(): Promise<void> {
    // Idempotent: return early if already initialized
    if (this.initialized) {
      return;
    }

    try {
      // Try to load persisted data
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        // Load last error
        try {
          const lastErrorJson = await OfficeRuntime.storage.getItem(
            STORAGE_KEYS.DIAG_LAST_ERROR
          );
          if (lastErrorJson) {
            const parsed = JSON.parse(lastErrorJson as string);
            if (parsed.schema_version === STORAGE_SCHEMA_VERSION && parsed.at_iso) {
              this.lastError = parsed as DiagnosticError;
            }
          }
        } catch (e) {
          // Swallow error, continue in-memory
        }

        // Load last request
        try {
          const lastRequestJson = await OfficeRuntime.storage.getItem(
            STORAGE_KEYS.DIAG_LAST_REQUEST
          );
          if (lastRequestJson) {
            const parsed = JSON.parse(lastRequestJson as string);
            if (
              parsed.schema_version === STORAGE_SCHEMA_VERSION &&
              parsed.request_id
            ) {
              this.lastRequest = parsed;
            }
          }
        } catch (e) {
          // Swallow error, continue in-memory
        }

        // Load session started time
        try {
          const sessionJson = await OfficeRuntime.storage.getItem(
            STORAGE_KEYS.DIAG_SESSION_STARTED
          );
          if (sessionJson) {
            const parsed = JSON.parse(sessionJson as string);
            if (
              parsed.schema_version === STORAGE_SCHEMA_VERSION &&
              parsed.value
            ) {
              this.sessionStartedAt = parsed.value;
            } else {
              // Invalid format, persist current session
              await this.persistSessionStarted();
            }
          } else {
            // No persisted session, persist current one
            await this.persistSessionStarted();
          }
        } catch (e) {
          // Swallow error, continue in-memory
        }
      }
    } catch (e) {
      // Swallow all errors, continue in-memory only
    } finally {
      this.initialized = true;
    }
  }

  /**
   * Record the start of an API call
   * Returns a callId for tracking
   */
  recordCallStart(op_key: string, endpoint: string): string {
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);
    const callId =
      Date.now().toString(36) + "-" + (this.callIdSeq++).toString(36);
    const startTime = Date.now();

    // Debug logging for PRICES.HISTORY (silenced in test mode)
    if (op_key === "PRICES.HISTORY") {
      logDebug("[DiagnosticsStore] recordCallStart op_key=PRICES.HISTORY endpoint=" + normalizedEndpoint);
    }

    this.inFlightCalls.set(callId, {
      startTime,
      op_key,
      endpoint: normalizedEndpoint,
    });

    // Update in-flight count for this op
    const stats = this.opStats.get(op_key) || {
      count: 0,
      sum_ms: 0,
      max_ms: 0,
      last_ms: 0,
      in_flight: 0,
    };
    stats.in_flight++;
    this.opStats.set(op_key, stats);

    return callId;
  }

  /**
   * Record the end of an API call
   */
  recordCallEnd(
    callId: string,
    payload: {
      ok: boolean;
      ms: number;
      request_id?: string;
      entitlements_request_id?: string;
      cache?: "hit" | "miss" | "bypass" | "stale" | "none";
      cacheBucket?: string;
      cacheKey?: string;
      sessionCache?: "hit" | "miss" | "join" | "bypass" | "none";
      sessionJoinCount?: number;
    }
  ): void {
    const { ok, ms, request_id, entitlements_request_id, cache, cacheBucket, cacheKey, sessionCache, sessionJoinCount } = payload;
    const inFlight = this.inFlightCalls.get(callId);
    if (!inFlight) {
      return; // Call not found (shouldn't happen, but be safe)
    }

    this.inFlightCalls.delete(callId);

    const { op_key, endpoint } = inFlight;

    // Update stats
    const stats = this.opStats.get(op_key) || {
      count: 0,
      sum_ms: 0,
      max_ms: 0,
      last_ms: 0,
      in_flight: 0,
    };

    stats.count++;
    stats.sum_ms += ms;
    stats.max_ms = Math.max(stats.max_ms, ms);
    stats.last_ms = ms;
    stats.in_flight = Math.max(0, stats.in_flight - 1); // Decrement in-flight

    this.opStats.set(op_key, stats);

    // Add to ring buffer
    const record: DiagnosticCallRecord = {
      at_iso: new Date().toISOString(),
      op_key,
      endpoint,
      ok,
      ms,
      request_id,
      entitlements_request_id,
      cache,
      cacheBucket,
      cacheKey,
      sessionCache,
      sessionJoinCount,
    };

    // Aggressive deduplication: If we have a request_id, ensure only one entry per request_id
    // Remove ALL existing entries with this request_id, then add the new one
    if (request_id) {
      const now = Date.now();
      const thirtySecondsAgo = now - 30000;
      
      // Remove ALL entries with this request_id (within 30 seconds) - keep only the newest
      this.recentCalls = this.recentCalls.filter((existing) => {
        if (!existing.request_id || existing.request_id !== request_id) {
          return true; // Keep entries with different or no request_id
        }
        const existingTime = new Date(existing.at_iso).getTime();
        // Remove if same request_id and within 30 seconds (keep only the newest)
        return existingTime < thirtySecondsAgo;
      });
    }

    this.recentCalls.push(record);
    
    // Enforce max size
    if (this.recentCalls.length > this.MAX_RECENT_CALLS) {
      this.recentCalls.shift(); // Remove oldest
    }
    
    // Final deduplication pass: If we still have duplicates with same request_id and timestamp,
    // keep only the first one (defensive - should not be needed after the filter above)
    if (request_id) {
      const seen = new Set<string>();
      this.recentCalls = this.recentCalls.filter((existing) => {
        if (!existing.request_id) return true;
        const key = existing.request_id + "|" + existing.at_iso;
        if (seen.has(key)) {
          return false; // Duplicate - remove
        }
        seen.add(key);
        return true;
      });
    }

    // Persist stats snapshot to storage (for cross-context visibility)
    // Debounce: only persist every N calls or after a delay
    this.persistStatsSnapshot().catch(() => {
      // Swallow errors
    });
  }

  /**
   * Record an error
   */
  recordError({
    category,
    message,
    op_key,
    endpoint,
    request_id,
  }: {
    category: DiagnosticErrorCategory;
    message: string;
    op_key: string;
    endpoint?: string;
    request_id?: string;
  }): void {
    const normalizedEndpoint = endpoint ? this.normalizeEndpoint(endpoint) : undefined;

    const error: DiagnosticError = {
      at_iso: new Date().toISOString(),
      category,
      message,
      op_key,
      endpoint: normalizedEndpoint,
      request_id,
    };

    this.lastError = error;

    // Persist to storage (best effort, never throw)
    this.persistLastError(error).catch(() => {
      // Swallow errors
    });
  }

  /**
   * Set the last request information
   */
  setLastRequest({
    request_id,
    op_key,
    endpoint,
    ms,
    ok,
  }: {
    request_id: string;
    op_key: string;
    endpoint: string;
    ms: number;
    ok: boolean;
  }): void {
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);

    this.lastRequest = {
      request_id,
      op_key,
      endpoint: normalizedEndpoint,
      ms,
      ok,
    };

    // Persist to storage (best effort, never throw)
    this.persistLastRequest(this.lastRequest).catch(() => {
      // Swallow errors
    });
  }

  /**
   * Clear all diagnostics data
   */
  async clear(): Promise<void> {
    this.opStats.clear();
    this.inFlightCalls.clear();
    this.recentCalls = [];
    this.lastError = null;
    this.lastRequest = null;
    this.sessionStartedAt = new Date().toISOString();
    this.callIdSeq = 0;
    this.initialized = false; // Reset so init() can run again
    this.cacheMetrics.clear();
    this.cacheEvents = [];

    // Clear persisted storage
    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DIAG_LAST_ERROR);
        await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DIAG_LAST_REQUEST);
        await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DIAG_SESSION_STARTED);
        await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DIAG_STATS_SNAPSHOT);
      }
    } catch (e) {
      // Swallow errors
    }
  }

  /**
   * Increment a cache metric counter
   */
  incrementCacheMetric(name: string): void {
    const current = this.cacheMetrics.get(name) || 0;
    this.cacheMetrics.set(name, current + 1);
  }

  /**
   * Record a cache event (for verbose debugging)
   */
  recordCacheEvent(event: string, key?: string): void {
    const truncatedKey = key && key.length > 50 ? key.substring(0, 50) : key;
    const cacheEvent = {
      at_iso: new Date().toISOString(),
      event,
      key: truncatedKey,
    };

    this.cacheEvents.push(cacheEvent);

    // Enforce max size (ring buffer)
    if (this.cacheEvents.length > this.MAX_CACHE_EVENTS) {
      this.cacheEvents.shift(); // Remove oldest
    }
  }

  /**
   * Get cache metrics as a record
   */
  getCacheMetrics(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [name, count] of this.cacheMetrics.entries()) {
      result[name] = count;
    }
    return result;
  }

  /**
   * Get cache events (for verbose debugging)
   */
  getCacheEvents(): Array<{ at_iso: string; event: string; key?: string }> {
    return [...this.cacheEvents];
  }

  /**
   * Get a snapshot of current diagnostics (redacted, safe for export)
   */
  getSnapshot(): DiagnosticsSnapshot {
    // Convert internal stats to public format (calculate avg_ms)
    const stats: Record<string, DiagnosticOpStats> = {};
    for (const [op_key, internalStats] of Array.from(this.opStats.entries())) {
      stats[op_key] = {
        count: internalStats.count,
        in_flight: internalStats.in_flight,
        last_ms: internalStats.last_ms,
        avg_ms:
          internalStats.count > 0
            ? Math.round(internalStats.sum_ms / internalStats.count)
            : 0,
        max_ms: internalStats.max_ms,
      };
    }

    const officeInfo = this.getOfficeInfo();

    // Return recent calls sorted by timestamp (newest first)
    const recentSorted = [...this.recentCalls].sort((a, b) => {
      const timeA = new Date(a.at_iso).getTime();
      const timeB = new Date(b.at_iso).getTime();
      return timeB - timeA; // Descending (newest first)
    });

    // Get cache metrics
    const cacheMetrics = this.getCacheMetrics();

    // Check if cache events should be included (behind feature flag)
    // We need to check config, but we can't easily access it here
    // For now, we'll include cache_metrics always, and cache_events only if there are any
    // The actual filtering by feature flag should be done at the UI/diagnostics panel level
    const snapshot: DiagnosticsSnapshot = {
      app: {
        version: BUILD_INFO.version,
        env: BUILD_INFO.env,
      },
      office: officeInfo,
      session: {
        started_at: this.sessionStartedAt,
      },
      last_request: this.lastRequest,
      last_error: this.lastError,
      stats,
      recent: recentSorted, // Sorted newest first
    };

    // Always include cache metrics if any exist
    if (Object.keys(cacheMetrics).length > 0) {
      snapshot.cache_metrics = cacheMetrics;
    }

    // Include cache events (filtering by feature flag should be done at UI level)
    if (this.cacheEvents.length > 0) {
      snapshot.cache_events = [...this.cacheEvents];
    }

    return snapshot;
  }

  /**
   * Merge stats from storage and return merged snapshot
   * This should be called before getSnapshot() to ensure cross-context data is included
   */
  async mergeStatsFromStorage(): Promise<void> {
    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        const snapshotJson = await OfficeRuntime.storage.getItem(
          STORAGE_KEYS.DIAG_STATS_SNAPSHOT
        );
        if (snapshotJson) {
          const parsed = JSON.parse(snapshotJson as string);
          
          // Debug logging (silenced in test mode)
          logDebug("[DiagnosticsStore] mergeStatsFromStorage found snapshot with " + (parsed.stats ? Object.keys(parsed.stats).length : 0) + " ops");
          if (parsed.stats && parsed.stats["PRICES.HISTORY"]) {
            logDebug("[DiagnosticsStore] found PRICES.HISTORY in storage count=" + parsed.stats["PRICES.HISTORY"].count);
          }
          
          if (
            parsed.schema_version === STORAGE_SCHEMA_VERSION &&
            parsed.stats
          ) {
            // Merge stats into internal storage (take max values)
            const storedStatsEntries = Object.entries(parsed.stats) as [string, DiagnosticOpStats][];
            for (const [op_key, storedStats] of storedStatsEntries) {
              const current = this.opStats.get(op_key);
              if (current) {
                // Merge: take the higher values and combine sums
                this.opStats.set(op_key, {
                  count: Math.max(current.count, storedStats.count),
                  sum_ms: Math.max(current.sum_ms, storedStats.count * storedStats.avg_ms),
                  max_ms: Math.max(current.max_ms, storedStats.max_ms),
                  last_ms: Math.max(current.last_ms, storedStats.last_ms),
                  in_flight: Math.max(current.in_flight, storedStats.in_flight),
                });
              } else {
                // New op_key from storage - create internal stats
                this.opStats.set(op_key, {
                  count: storedStats.count,
                  sum_ms: storedStats.count * storedStats.avg_ms,
                  max_ms: storedStats.max_ms,
                  last_ms: storedStats.last_ms,
                  in_flight: storedStats.in_flight,
                });
              }
            }

            // Merge recent calls (combine and sort by timestamp, deduplicate by request_id, keep last 25)
            if (parsed.recent && Array.isArray(parsed.recent)) {
              const allRecent = [...this.recentCalls, ...parsed.recent];
              
              // Deduplicate by request_id + timestamp (keep only one entry per unique request_id)
              const seen = new Map<string, DiagnosticCallRecord>(); // request_id -> most recent record
              for (const record of allRecent) {
                if (record.request_id) {
                  const existing = seen.get(record.request_id);
                  if (!existing) {
                    seen.set(record.request_id, record);
                  } else {
                    // Keep the one with the newer timestamp
                    const existingTime = new Date(existing.at_iso).getTime();
                    const recordTime = new Date(record.at_iso).getTime();
                    if (recordTime > existingTime) {
                      seen.set(record.request_id, record);
                    }
                  }
                } else {
                  // No request_id - keep all (can't deduplicate)
                  seen.set(record.at_iso + "|" + record.op_key, record);
                }
              }
              
              // Convert back to array and sort by timestamp (newest first)
              const deduplicated = Array.from(seen.values());
              deduplicated.sort((a, b) => {
                const timeA = new Date(a.at_iso).getTime();
                const timeB = new Date(b.at_iso).getTime();
                return timeB - timeA;
              });
              
              // Keep last 25
              this.recentCalls = deduplicated.slice(0, 25);
            }
          }
        }
      }
    } catch (e) {
      // Swallow errors
    }
  }


  /**
   * Normalize an endpoint to a safe path-only format
   * Exposed for testing
   */
  normalizeEndpoint(endpoint: string): string {
    let path: string;

    // Handle absolute URLs
    if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
      try {
        const url = new URL(endpoint);
        path = url.pathname;
      } catch (e) {
        // If URL parsing fails, assume it's already a path
        path = endpoint;
      }
    } else {
      // Assume it's already a path
      path = endpoint;
    }

    // Ensure starts with /
    if (!path.startsWith("/")) {
      path = "/" + path;
    }

    // Collapse multiple slashes: /v1//test///ping → /v1/test/ping
    path = path.replace(/\/+/g, "/");

    // Strip query string (everything after ?)
    const queryIndex = path.indexOf("?");
    if (queryIndex !== -1) {
      path = path.substring(0, queryIndex);
    }

    // Cap at 200 chars
    if (path.length > 200) {
      path = path.substring(0, 200);
    }

    return path;
  }

  /**
   * Get Office host and platform info
   */
  private getOfficeInfo(): { host: string; platform: string } {
    try {
      if (typeof Office !== "undefined" && Office.context) {
        return {
          host:
            Office.context.host?.toString() ||
            (Office.context as any).host ||
            "unknown",
          platform:
            Office.context.platform?.toString() ||
            (Office.context as any).platform ||
            "unknown",
        };
      }
    } catch (e) {
      // Swallow errors
    }
    return { host: "unknown", platform: "unknown" };
  }

  /**
   * Persist last error to storage
   */
  private async persistLastError(error: DiagnosticError): Promise<void> {
    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        const blob = {
          schema_version: STORAGE_SCHEMA_VERSION,
          ...error,
        };
        await OfficeRuntime.storage.setItem(
          STORAGE_KEYS.DIAG_LAST_ERROR,
          JSON.stringify(blob)
        );
      }
    } catch (e) {
      // Swallow errors
    }
  }

  /**
   * Persist last request to storage
   */
  private async persistLastRequest(request: {
    request_id: string;
    op_key: string;
    endpoint: string;
    ms: number;
    ok: boolean;
  }): Promise<void> {
    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        const blob = {
          schema_version: STORAGE_SCHEMA_VERSION,
          ...request,
        };
        await OfficeRuntime.storage.setItem(
          STORAGE_KEYS.DIAG_LAST_REQUEST,
          JSON.stringify(blob)
        );
      }
    } catch (e) {
      // Swallow errors
    }
  }

  /**
   * Persist session started time to storage
   */
  private async persistSessionStarted(): Promise<void> {
    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        const blob = {
          schema_version: STORAGE_SCHEMA_VERSION,
          value: this.sessionStartedAt,
        };
        await OfficeRuntime.storage.setItem(
          STORAGE_KEYS.DIAG_SESSION_STARTED,
          JSON.stringify(blob)
        );
      }
    } catch (e) {
      // Swallow errors
    }
  }

  /**
   * Persist stats snapshot to storage (for cross-context visibility)
   * Debounced to avoid excessive writes
   */
  private persistStatsSnapshotTimeout: number | null = null;
  private async persistStatsSnapshot(): Promise<void> {
    // Debounce: clear existing timeout
    if (this.persistStatsSnapshotTimeout !== null) {
      if (typeof clearTimeout !== "undefined") {
        clearTimeout(this.persistStatsSnapshotTimeout);
      }
    }

    // Set new timeout (persist after 500ms of inactivity)
    const setTimeoutFn = typeof setTimeout !== "undefined" ? setTimeout : (typeof window !== "undefined" && window.setTimeout) ? window.setTimeout : null;
    if (!setTimeoutFn) {
      // No setTimeout available, persist immediately
      await this.doPersistStatsSnapshot();
      return;
    }

    this.persistStatsSnapshotTimeout = setTimeoutFn(async () => {
      try {
        if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
          // Convert internal stats to public format
          const stats: Record<string, DiagnosticOpStats> = {};
          for (const [op_key, internalStats] of Array.from(this.opStats.entries())) {
            stats[op_key] = {
              count: internalStats.count,
              in_flight: internalStats.in_flight,
              last_ms: internalStats.last_ms,
              avg_ms:
                internalStats.count > 0
                  ? Math.round(internalStats.sum_ms / internalStats.count)
                  : 0,
              max_ms: internalStats.max_ms,
            };
          }

          const blob = {
            schema_version: STORAGE_SCHEMA_VERSION,
            stats,
            recent: [...this.recentCalls],
          };
          await OfficeRuntime.storage.setItem(
            STORAGE_KEYS.DIAG_STATS_SNAPSHOT,
            JSON.stringify(blob)
          );
        }
      } catch (e) {
        // Swallow errors
      }
    }, 500) as unknown as number;
  }

  /**
   * Actually persist the stats snapshot to storage
   */
  private async doPersistStatsSnapshot(): Promise<void> {
    try {
      if (typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage) {
        // Convert internal stats to public format
        const stats: Record<string, DiagnosticOpStats> = {};
        for (const [op_key, internalStats] of Array.from(this.opStats.entries())) {
          stats[op_key] = {
            count: internalStats.count,
            in_flight: internalStats.in_flight,
            last_ms: internalStats.last_ms,
            avg_ms:
              internalStats.count > 0
                ? Math.round(internalStats.sum_ms / internalStats.count)
                : 0,
            max_ms: internalStats.max_ms,
          };
        }

        const blob = {
          schema_version: STORAGE_SCHEMA_VERSION,
          stats,
          recent: [...this.recentCalls],
        };
        
        // Debug logging for PRICES.HISTORY
        // Debug logging (silenced in test mode)
        if (stats["PRICES.HISTORY"]) {
          logDebug("[DiagnosticsStore] persisting stats snapshot with PRICES.HISTORY count=" + stats["PRICES.HISTORY"].count);
        }
        
        await OfficeRuntime.storage.setItem(
          STORAGE_KEYS.DIAG_STATS_SNAPSHOT,
          JSON.stringify(blob)
        );
      }
    } catch (e) {
      // Swallow errors
    }
  }
}

// Export singleton instance
export const diagnosticsStore = new DiagnosticsStore();

// Export helper functions
export function safeErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    // If it has a custom name (not the default "Error"), include it
    if (err.name && err.name !== "Error" && err.message) {
      return err.name + ": " + err.message;
    }
    // Otherwise return message only (no stack, no "Error: " prefix)
    return err.message || err.name || "Unknown error";
  }
  if (typeof err === "string") {
    return err;
  }
  // Handle plain objects with message property
  if (err && typeof err === "object" && "message" in err && typeof (err as any).message === "string") {
    return (err as any).message;
  }
  return "Unknown error";
}

export function categorizeError(
  err: unknown,
  status?: number
): DiagnosticErrorCategory {
  // Network errors
  if (err instanceof Error && err.name === "AbortError") {
    return "network";
  }
  if (status === 0) {
    return "network";
  }

  // Auth errors
  if (status === 401 || status === 403) {
    return "auth";
  }

  // Not found errors (404)
  if (status === 404) {
    return "not_found";
  }

  // Backend errors
  if (status !== undefined && status >= 500) {
    return "backend";
  }

  // Excel errors (could be added later if we detect Excel-specific errors)
  // For now, no specific detection

  // Validation errors (could be added later if we detect validation errors)
  // For now, no specific detection

  return "unknown";
}
